import { getCache, getCacheKey, setCache } from 'lib/cache';
import { getURI } from 'lib/graphql/apolloClient';
import { appendAkamaiHeaders } from 'lib/utilities/akamaiHeaders';
import { logError, logInfo } from 'lib/utilities/logger';
import { BrandCode } from 'types';
import { AemContentType } from '../../../../../types/AEMContent';
import { pathToMarket } from './utils';

const getLegacyCELRequestQuery = (componentName, path, countryAlpha2Code, languageCode) => {
	const country = path.includes('int') ? 'int' : countryAlpha2Code;
	return `share.${componentName.toLowerCase()}.${country.toLowerCase()}.${languageCode.toLowerCase()}.html`;
};

const getCELRequestQuery = (componentName, market, country, language) => {
	const isHeader = componentName === AemContentType.HEADER;
	return `${market.toLowerCase()}/${country.toLowerCase()}/${language.toLowerCase()}/${componentName.toLowerCase()}.html${isHeader ? '?isStatic=true' : ''}`;
};

const getLegacyRCIRequestQuery = (componentName, countryCode, marketCode, languageCode) => {
	return `share.${componentName.toLowerCase()}.${countryCode}.${marketCode.toUpperCase()}.${languageCode.toUpperCase()}.html`;
};

const getRCIRequestQuery = (componentName, market, country, language) => {
	const isHeader = componentName === AemContentType.HEADER;
	return `${market.toLowerCase()}/${country.toLowerCase()}/${language.toLowerCase()}/${componentName.toLowerCase()}.html${isHeader ? '?isStatic=true' : ''}`;
};

const getRequestQuery = (brandCode, componentName, locale, enableNewHeaderFooter) => {
	const { country, language, market, path, countryAlpha2Code } = locale;
	const formatLanguage = language.split('-')[0];

	if (brandCode === BrandCode.Celebrity) {
		const celMarket = pathToMarket(path, brandCode);
		return enableNewHeaderFooter
			? getCELRequestQuery(componentName, celMarket, country, formatLanguage)
			: getLegacyCELRequestQuery(componentName, path, countryAlpha2Code, formatLanguage);
	}

	return enableNewHeaderFooter
		? getRCIRequestQuery(componentName, market, country, formatLanguage)
		: getLegacyRCIRequestQuery(componentName, country, market, formatLanguage);
};

const getComponentPath = (host, query, endpoints, enableNewHeaderFooter) => {
	return enableNewHeaderFooter ? getURI(host, query) : getURI(host, `${endpoints.universalNavigationApi}${query}`);
};

const getComponentAsText = async (componentName, locale, config, host, enableNewHeaderFooter) => {
	const { brand, endpoints } = config;
	const query = getRequestQuery(brand.code, componentName, locale, enableNewHeaderFooter);

	const path = getComponentPath(host, query, endpoints, enableNewHeaderFooter);

	logInfo(`[Universal Navigation] Fetching AEM ${componentName} with flag ${enableNewHeaderFooter} from: ${path}`, { componentName, path });

	return fetch(path, { method: 'GET', headers: appendAkamaiHeaders({ 'Cache-Control': 'no-store' }) })
		.then((response) => {
			if (!response.ok) {
				throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
			}
			return response.text();
		})
		.catch((error) => {
			logError(`[Universal Navigation] Error retrieving the AEM ${componentName}: ${error.message}`, {
				brandCode: brand.code,
				componentName,
				path,
				error,
			});

			return null;
		});
};

export const getFooter = async (locale, config, host, enableNewHeaderFooter) =>
	getComponentAsText(AemContentType.FOOTER, locale, config, host, enableNewHeaderFooter);

export const getHeader = async (locale, config, host, enableNewHeaderFooter) =>
	getComponentAsText(AemContentType.HEADER, locale, config, host, enableNewHeaderFooter);

export const getUniversalNavigation = async (locale, config, logId, host, enableNewHeaderFooter) => {
	const { brand } = config;
	const cacheKey = getCacheKey('aem_universal_navigation', [brand.code, locale.country, locale.market, locale.language]);
	const cachedData = getCache(cacheKey);

	if (cachedData) {
		logInfo('[Server Cache] AEM Universal Navigation Cache Hit', { cacheKey, logId });

		return cachedData;
	}

	logInfo('[Server Cache] AEM Universal Navigation Cache Miss', { cacheKey, logId });

	const [headerHTML, footerHTML] = await Promise.all([
		getHeader(locale, config, host, enableNewHeaderFooter),
		getFooter(locale, config, host, enableNewHeaderFooter),
	]);
	const universalNavigationHTML = {
		header: headerHTML,
		footer: footerHTML,
	};
	setCache(cacheKey, universalNavigationHTML);

	return universalNavigationHTML;
};
